import { z } from "zod";
import { PHONE_FORMAT_ERROR } from "../constants";

/** Matches Angular `zipcodeValidator` (`customvalidation.ts`): non-empty value must have length 5 or 9 (no trim). */
export function isZipcodeValidAngularStyle(val: string): boolean {
  return val.length === 5 || val.length === 9;
}

/** Matches Angular `mobileValidator` (`customvalidation.ts`): strip non-digits, then same regex. Empty value is valid (required is separate). */
export function isMobileValidAngularStyle(val: string | undefined): boolean {
  const raw = val ?? "";
  if (raw === "") return true;
  const mobile = ("" + raw).replace(/\D/g, "");
  const match = mobile.match(/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/);
  return !!match;
}

/** Angular `validateData`: strip non-digits from `company_phone` / `company_office_phone` before POST. */
export function stripPhoneDigitsForApi(val: string | number | undefined | null): string {
  if (val === undefined || val === null) return "";
  return String(val).replace(/\D/g, "");
}

export const companyAddSchema = z.object({
    company_name: z.string().min(1, "Company Name is required"),
    office_phone: z
        .string()
        .optional()
        .refine((val) => isMobileValidAngularStyle(val), PHONE_FORMAT_ERROR),
    contact_name: z.string().min(1, "Primary Contact is required"),
    contact_mobile: z
        .string()
        .min(1, "Primary Phone is required")
        .refine((val) => isMobileValidAngularStyle(val), PHONE_FORMAT_ERROR),
    address1: z.string().min(1, "Address Line1 is required"),
    address2: z.string().optional(),
    city: z.string().min(1, "Company City is required"),
    state: z.string().min(1, "Company State is required"),
    zipcode: z
        .string()
        .min(1, "Zipcode is required.")
        .refine(
            (val) => isZipcodeValidAngularStyle(val),
            "Enter Valid Zipcode, Should match pattern [XXXXX] or [XXXXX-XXXX].",
        ),

    // New fields
    listed: z.string().min(1, "Active status is required"),
    listed_ids: z.array(z.string()).optional(),
    email_frequency: z.string().min(1, "Email Frequency is required"),
    latitude: z.string().or(z.number()).optional(),
    longitude: z.string().or(z.number()).optional(),
    // New fields from Edit page
    legal_name: z.string().optional(),
    legal_verified: z.string().optional(),
    c_aln_ref_id: z.string().or(z.number()).optional(),
    company_alias_name: z.string().optional(),
    // Federal ID
    federal_id: z.string().optional(),
    // Mailing address toggle + fields
    is_mailing_address: z.boolean().optional(),
    mailing_address1: z.string().optional(),
    mailing_address2: z.string().optional(),
    mailing_city: z.string().optional(),
    mailing_state: z.string().optional(),
    mailing_zipcode: z.string().optional(),
    // Management
    company_managed: z.string().optional(),
    property_mgmt_system: z.array(z.string()).optional(),
    property_mgmt_system_other: z.string().optional(),
    resident_screening: z.array(z.string()).optional(),
    resident_screening_other: z.string().optional(),
}).superRefine((data, ctx) => {
        applyPreferencesOtherTextRequiredWhenOtherSelected(data, ctx);
    });

/**
 * Angular `company-edit`: when multiselect includes `Other`, `*_other` controls use `Validators.required`.
 * Angular `Validators.required` accepts whitespace-only (does not trim) — only `''` / null / undefined fail.
 */
export function applyPreferencesOtherTextRequiredWhenOtherSelected(
    data: {
        property_mgmt_system?: string[] | null | undefined;
        property_mgmt_system_other?: string | null | undefined;
        resident_screening?: string[] | null | undefined;
        resident_screening_other?: string | null | undefined;
    },
    ctx: z.RefinementCtx,
): void {
    if (data.property_mgmt_system?.includes("Other")) {
        const text = data.property_mgmt_system_other ?? "";
        if (text === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Other is required.",
                path: ["property_mgmt_system_other"],
            });
        }
    }
    if (data.resident_screening?.includes("Other")) {
        const text = data.resident_screening_other ?? "";
        if (text === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Other is required.",
                path: ["resident_screening_other"],
            });
        }
    }
}

/** Company edit Preferences tab — same conditional required as Angular / `companyAddSchema`. */
export const companyPreferencesTabSchema = z
    .object({
        property_mgmt_system: z.array(z.string()),
        property_mgmt_system_other: z.string().optional(),
        resident_screening: z.array(z.string()),
        resident_screening_other: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        applyPreferencesOtherTextRequiredWhenOtherSelected(data, ctx);
    });

/**
 * Angular `submitForm()` validates the full `companyEditForm`, not only Preferences controls.
 * Core always-required edit fields + Preferences Other (same as FormGroup + `Validators.required`).
 */
export const companyEditSaveParitySchema = z
    .object({
        company_name: z.string().min(1, "Company Name is required"),
        legal_name: z.string().min(1, "Legal Name is required"),
        company_address: z.string().min(1, "Address Line1 is required"),
        company_city: z.string().min(1, "Company City is required"),
        company_state: z.string().min(1, "Company State is required"),
        company_zipcode: z
            .string()
            .min(1, "Zipcode is required.")
            .refine(
                (val) => isZipcodeValidAngularStyle(val),
                "Enter Valid Zipcode, Should match pattern [XXXXX] or [XXXXX-XXXX].",
            ),
        email_frequency: z.string().min(1, "Email Frequency is required"),
        listed: z.string().min(1, "Active status is required"),
        company_phone: z
            .string()
            .optional()
            .refine((val) => isMobileValidAngularStyle(val), PHONE_FORMAT_ERROR),
        company_office_phone: z
            .string()
            .optional()
            .refine((val) => isMobileValidAngularStyle(val), PHONE_FORMAT_ERROR),
        property_mgmt_system: z.array(z.string()),
        property_mgmt_system_other: z.string().optional(),
        resident_screening: z.array(z.string()),
        resident_screening_other: z.string().optional(),
        lease_start_lead_days: z.union([z.number(), z.string()]).nullish(),
    })
    .superRefine((data, ctx) => {
        applyPreferencesOtherTextRequiredWhenOtherSelected(data, ctx);
    });

/** Map GET company + Preferences form into `companyEditSaveParitySchema` input. */
export function buildCompanyEditSaveParityInput(
    company: Record<string, unknown> | null | undefined,
    preferences: {
        property_mgmt_system: string[];
        property_mgmt_system_other?: string;
        resident_screening: string[];
        resident_screening_other?: string;
        lease_start_lead_days?: number | string | null;
    },
): z.input<typeof companyEditSaveParitySchema> {
    return {
        company_name: String(company?.company_name ?? ""),
        legal_name: String(company?.legal_name ?? company?.company_legal_name ?? ""),
        company_address: String(company?.company_address ?? company?.address1 ?? ""),
        company_city: String(company?.company_city ?? company?.city ?? ""),
        company_state: String(company?.company_state ?? company?.state ?? ""),
        company_zipcode: String(company?.company_zipcode ?? company?.zipcode ?? ""),
        email_frequency: String(company?.email_frequency ?? ""),
        listed: String(company?.listed ?? ""),
        company_phone: String(company?.company_phone ?? company?.contact_mobile ?? ""),
        company_office_phone: String(company?.company_office_phone ?? company?.office_phone ?? ""),
        property_mgmt_system: preferences.property_mgmt_system,
        property_mgmt_system_other: preferences.property_mgmt_system_other ?? "",
        resident_screening: preferences.resident_screening,
        resident_screening_other: preferences.resident_screening_other ?? "",
        lease_start_lead_days: preferences.lease_start_lead_days,
    };
}

/**
 * Angular `editCompany()` after `getFilterValue`: force `*_other = ''` when multiselect is
 * non-empty and does not include `Other` (so FormData still sends the empty key).
 * Empty multiselect: omit leftover `*_other` (Angular removed the control).
 */
export function applyPreferencesOtherClearAngularStyle(
    requestData: Record<string, unknown>,
): void {
    const pms = requestData.property_mgmt_system;
    if (Array.isArray(pms) && pms.length > 0 && !pms.includes("Other")) {
        requestData.property_mgmt_system_other = "";
    } else if (!Array.isArray(pms) || !pms.includes("Other")) {
        delete requestData.property_mgmt_system_other;
    }
    const rs = requestData.resident_screening;
    if (Array.isArray(rs) && rs.length > 0 && !rs.includes("Other")) {
        requestData.resident_screening_other = "";
    } else if (!Array.isArray(rs) || !rs.includes("Other")) {
        delete requestData.resident_screening_other;
    }
}


/**
 * Mirrors Angular `company-edit.component.ts` `getFilterValue()`:
 * trim strings; only include keys where value is truthy and not `''` after trim.
 * Arrays (including empty `[]`) are always copied — Angular keeps empty notice multiselect arrays for `multiSelect()`.
 * Empty optional fields (e.g. `company_address2`) are omitted when string-only.
 */
export function getFilterValueAngularStyle(params: Record<string, unknown>): Record<string, unknown> {
  const filterParams: Record<string, unknown> = {}
  for (const filterKey of Object.keys(params)) {
    if (!Object.prototype.hasOwnProperty.call(params, filterKey)) continue
    let val = params[filterKey]
    if (Array.isArray(val)) {
      filterParams[filterKey] = val
      continue
    }
    if (typeof val === "string") val = val.trim()
    if (val && val !== "") {
      filterParams[filterKey] = val
    }
  }
  return filterParams
}

/** Angular `setOrganization()` / `listed_ids` FormControl: org ids from GET `company.listed_ids` rows. */
function listedIdsFromCompany(company: Record<string, unknown> | null | undefined): string[] {
  const raw = company?.listed_ids
  if (!Array.isArray(raw) || raw.length === 0) return []
  const out: string[] = []
  for (const row of raw) {
    if (row != null && typeof row === "object" && !Array.isArray(row) && "org_id" in row) {
      const id = (row as { org_id?: unknown }).org_id
      if (id != null && String(id).trim() !== "") out.push(String(id).trim())
    } else if (typeof row === "string" || typeof row === "number") {
      const s = String(row).trim()
      if (s !== "") out.push(s)
    }
  }
  return out
}

/** Angular `pmc_invite` FormControl default `'1'` (Required); preserved on tab saves when UI is hidden. */
export function resolvePmcInviteParity(
  company: Record<string, unknown> | null | undefined,
): string {
  const raw = company?.pmc_invite
  if (raw != null && String(raw).trim() !== "") return String(raw).trim()
  return "1"
}

/**
 * Keys Angular includes on `editCompany()` alongside tab fields — use for view-only tabs (e.g. Activity)
 * so `POST /api/company/:id` matches server expectations. Shallow-merges onto optional `base`.
 */
export function mergeAngularFullFormParityFields(
  company: Record<string, unknown> | null | undefined,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  const payload = { ...base }
  payload.federal_id =
    company?.federal_id != null && String(company.federal_id).trim() !== ""
      ? String(company.federal_id).trim()
      : ""
  if (company?.listed != null && String(company.listed).trim() !== "") {
    payload.listed = String(company.listed).trim()
  }
  if (company?.company_name != null && String(company.company_name).trim() !== "") {
    payload.company_name = String(company.company_name).trim()
  }
  if (company?.c_aln_ref_id != null && String(company.c_aln_ref_id).trim() !== "") {
    payload.c_aln_ref_id = company.c_aln_ref_id
  }
  if (company?.website_url != null && String(company.website_url).trim() !== "") {
    payload.website_url = company.website_url
  }
  payload.is_mailing_address =
    company?.is_mailing_address === "1" ||
    company?.is_mailing_address === true ||
    company?.is_mailing_address === 1
      ? 1
      : 0
  payload.check_exist = true
  payload.pmc_invite = resolvePmcInviteParity(company)

  /** Same POST context as Angular `editCompany()` — org scope + multi-org listings when present on GET company. */
  if (company?.org_id != null && String(company.org_id).trim() !== "") {
    payload.org_id = String(company.org_id).trim()
  }
  const listedIds = listedIdsFromCompany(company)
  if (listedIds.length > 0) {
    payload.listed_ids = listedIds
  }

  return payload
}

